import {
  CUSTOM_PAPER_MAX_MM,
  CUSTOM_PAPER_MIN_MM,
  PAGE_WIDTHS,
  PAPER_GROUPS,
  PAPER_METADATA,
  PAPER_MM,
  PAPER_UNITS,
  type PaperGroup,
  type PaperUnit,
  NAMED_PAPERS as SEGNAVIA_PAPERS,
  sheetMm,
} from '@segnavia/format'
import type { FAMILY } from './fonts/manifest'

export type NamedPaper = (typeof SEGNAVIA_PAPERS)[number]
export type { PaperGroup, PaperUnit }
export { CUSTOM_PAPER_MAX_MM, CUSTOM_PAPER_MIN_MM, PAPER_GROUPS, PAPER_UNITS }

/* Segnavia names several papers alike (four postcards, four posters) and
   tells them apart by size, so a label that would repeat carries its size. */
function sharesItsName(name: NamedPaper): boolean {
  const { name: label } = PAPER_METADATA[name]
  return SEGNAVIA_PAPERS.some(
    (other) => other !== name && PAPER_METADATA[other].name === label,
  )
}

/**
 * Every paper Segnavia prints, as this platform reads it. Segnavia holds the
 * list, so a paper it adds is offered here by a version bump.
 */
export const NAMED_PAPERS = Object.fromEntries(
  SEGNAVIA_PAPERS.map((name) => [
    name,
    {
      widthMm: PAPER_MM[name].width,
      heightMm: PAPER_MM[name].height,
      label: sharesItsName(name)
        ? `${PAPER_METADATA[name].name} ${PAPER_METADATA[name].size}`
        : PAPER_METADATA[name].name,
      group: PAPER_METADATA[name].group,
      size: PAPER_METADATA[name].size,
    },
  ]),
) as Record<
  NamedPaper,
  {
    widthMm: number
    heightMm: number
    label: string
    group: PaperGroup
    size: string
  }
>

/** How a person reads each orientation. */
export const ORIENTATION_LABELS = {
  portrait: 'Portrait',
  landscape: 'Landscape',
} as const

/**
 * A custom size is held in millimeters, with the unit it was typed in so it
 * reopens as typed. A size stored without one was typed in millimeters.
 */
export type HtmlPagePaper =
  | { name: NamedPaper }
  | { name: 'custom'; widthMm: number; heightMm: number; unit?: PaperUnit }
export type HtmlPageOrientation = 'portrait' | 'landscape'
export type HtmlPageAppFont = keyof typeof FAMILY
/** The longest family name a page's sheet may name as a local face. */
export const HTML_PAGE_LOCAL_FAMILY_MAX_CHARS = 200
/** The most local family names one assistant call may carry. */
export const LOCAL_FONT_FAMILY_MAX_COUNT = 500

export type HtmlPageLocalFont = {
  local: string
  fallback: HtmlPageAppFont
}
export type HtmlPageFont = HtmlPageAppFont | HtmlPageLocalFont
export type HtmlPageFonts = HtmlPageFont[]
export const NAMED_PAPER_NAMES = [...SEGNAVIA_PAPERS] as [
  NamedPaper,
  ...NamedPaper[],
]
export const NAMED_PAPER_SQL_LIST = NAMED_PAPER_NAMES.map(
  (name) => `'${name}'`,
).join(', ')
export const PAPER_UNIT_SQL_LIST = PAPER_UNITS.map((unit) => `'${unit}'`).join(
  ', ',
)

export type HtmlPagePaperGeometry = { widthMm: number; heightMm: number }

/** A paper's two sides in millimeters, in the order it names them. */
export function htmlPagePaperGeometry(
  paper: HtmlPagePaper | null,
): HtmlPagePaperGeometry | undefined {
  if (!paper) return undefined
  return paper.name === 'custom'
    ? { widthMm: paper.widthMm, heightMm: paper.heightMm }
    : {
        widthMm: NAMED_PAPERS[paper.name].widthMm,
        heightMm: NAMED_PAPERS[paper.name].heightMm,
      }
}

/**
 * The sheet a page prints on, by Segnavia's rule, the one the PDF is sized
 * by: landscape puts the longer side across and portrait the shorter,
 * whatever order the sides were given in.
 */
export function htmlPageSheetGeometry(
  paper: HtmlPagePaperGeometry | null | undefined,
  orientation: HtmlPageOrientation,
): HtmlPagePaperGeometry | undefined {
  if (!paper) return undefined
  const sheet = sheetMm({
    paper: { width: paper.widthMm, height: paper.heightMm, unit: 'mm' },
    orientation,
  })
  return { widthMm: sheet.width, heightMm: sheet.height }
}

/** A screen page has no paper, so it lies on a board at a window's shape. */
export const SCREEN_PAGE_SIZE = { width: 1280, height: 800 } as const

/**
 * How wide a screen page is set: the whole window, Segnavia's named measures,
 * or a number of CSS pixels. A page with paper ignores it.
 */
export type HtmlPageWidth = 'full' | 'standard' | 'wide' | number
export const NAMED_PAGE_WIDTHS = ['full', 'standard', 'wide'] as const
export const NAMED_PAGE_WIDTH_SQL_LIST = NAMED_PAGE_WIDTHS.map(
  (width) => `'${width}'`,
).join(', ')
/** A pixel width narrower than a small phone or wider than a large screen is refused. */
export const HTML_PAGE_WIDTH_MIN_PX = 320
export const HTML_PAGE_WIDTH_MAX_PX = 3840

/**
 * The screens a page is commonly made for, each a pixel width and the height
 * of that screen, which is the shape the page lies at on a board.
 */
export const SCREEN_PRESETS = {
  phone: { label: 'Phone', width: 390, height: 844 },
  tablet: { label: 'Tablet', width: 768, height: 1024 },
  desktop: { label: 'Desktop', width: 1280, height: 800 },
} as const
export type ScreenPreset = keyof typeof SCREEN_PRESETS

/** The named measures, as Segnavia sets them and a person reads them. */
export const NAMED_PAGE_WIDTH_LABELS: Record<
  (typeof NAMED_PAGE_WIDTHS)[number],
  string
> = {
  full: 'Full width',
  standard: 'Standard',
  wide: 'Wide',
}

/** A screen page's width as a person reads it: a preset by name, else the number. */
export function htmlPageWidthLabel(width: HtmlPageWidth): string {
  if (width === 'full') return NAMED_PAGE_WIDTH_LABELS.full
  if (typeof width !== 'number')
    return `${NAMED_PAGE_WIDTH_LABELS[width]} (${PAGE_WIDTHS[width]} px)`
  const preset = Object.values(SCREEN_PRESETS).find((p) => p.width === width)
  return preset ? `${preset.label} (${width} px)` : `${width} px`
}

/**
 * The shape a screen page lies at on a board, which draws nothing taller than
 * it is told. A preset's width lies at its screen's height; any other width at
 * the shape of the nearest screen, the wider on a tie, so a narrow page stands
 * tall like a phone and a wide one lies like a desktop.
 */
export function screenPageShape(width: HtmlPageWidth): {
  width: number
  height: number
} {
  if (width === 'full') return SCREEN_PAGE_SIZE
  const px = typeof width === 'number' ? width : PAGE_WIDTHS[width]
  const near = Object.values(SCREEN_PRESETS).reduce((best, p) =>
    Math.abs(p.width - px) <= Math.abs(best.width - px) ? p : best,
  )
  return { width: px, height: Math.round((px * near.height) / near.width) }
}
